// src/Components/Luna/useLuna.ts
// React side of the Luna power store: the equipped Luna's view (store first,
// inventory as the fallback), the store snapshot, and the activity session
// hook the runners use.
import { useContext, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { CharacterInventoryContext } from "../../Context/CharacterInventoryContext";
import type { CreatureDefinition, UserCreature } from "../../Context/CharacterInventoryContext";
import {
  EMPTY_COMBO,
  LunaView,
  progressFromXp,
  stageForLevel,
} from "./lunaModel";
import {
  beginLunaSession,
  getLunaState,
  holdLunaCelebrations,
  LunaStoreState,
  subscribeLuna,
} from "./lunaPowerStore";

export function useLunaStore(): LunaStoreState {
  return useSyncExternalStore(subscribeLuna, getLunaState, getLunaState);
}

export function stageNameOf(def: CreatureDefinition | undefined, stage: number): string {
  const names = (def as any)?.stageNames as Record<string, string> | undefined;
  return (names && (names[stage] || names[String(stage)])) || def?.name || "Your Luna";
}

export function stageImageOf(def: CreatureDefinition | undefined, stage: number): string | null {
  const assets = def?.stageAssets || [];
  const hit = assets.find((a) => Number(a.stage) === Number(stage));
  if (hit) return hit.image;
  const sorted = [...assets].sort((a, b) => b.stage - a.stage);
  return sorted.find((a) => a.stage <= stage)?.image || sorted[0]?.image || null;
}

/** Display view of an owned Luna from the inventory data. */
export function lunaViewFromInventory(def: CreatureDefinition | undefined, uc: UserCreature | undefined): LunaView | null {
  if (!uc) return null;
  const prog = progressFromXp(uc.xp || 0);
  const stage = Math.max(Number(uc.stage) || 1, def ? stageForLevel(def as any, prog.level) : 1);
  return {
    creatureId: uc.creatureId,
    name: stageNameOf(def, stage),
    image: stageImageOf(def, stage),
    stage,
    xp: Math.max(0, Number(uc.xp) || 0),
    level: prog.level,
    inLevel: prog.inLevel,
    nextLevelXp: prog.nextLevelXp,
    combo: EMPTY_COMBO,
  };
}

/** The equipped Luna from the inventory (null outside the provider or before it loads). */
export function useInventoryLuna(): LunaView | null {
  const inv = useContext(CharacterInventoryContext);
  const id = inv?.activeCreatureId || null;
  const uc = id ? inv?.ownedCreatureMap[id] : undefined;
  const def = id ? inv?.creatureDefinitions.find((d) => d.creatureId === id) : undefined;
  return useMemo(() => lunaViewFromInventory(def, uc), [def, uc]);
}

/** What the ring shows: the store's view of the equipped Luna, else the inventory's. */
export function useEquippedLuna(): LunaView | null {
  const store = useLunaStore();
  const inv = useInventoryLuna();
  if (store.shown && (!inv || store.shown.creatureId === inv.creatureId)) {
    // A newer inventory read (a swap, a refresh) wins over an older store view.
    if (inv && inv.xp > store.shown.xp) return { ...inv, combo: store.shown.combo };
    return store.shown;
  }
  return inv;
}

/**
 * For activity runners: start a Luna session when the run starts and hold the
 * evolution celebration while `running` is true, so it plays at the results.
 */
export function useLunaSession(running: boolean, key?: unknown) {
  useEffect(() => {
    beginLunaSession();
  }, [key]);
  const releaseRef = useRef<null | (() => void)>(null);
  useEffect(() => {
    if (running) {
      releaseRef.current = holdLunaCelebrations();
    }
    return () => {
      releaseRef.current?.();
      releaseRef.current = null;
    };
  }, [running]);
}
